import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import FidelityPaymentService from '../../services/fidelityPaymentService';
import type { PaymentData, PaymentResponse, PaymentStatusResponse, PaymentHistoryResponse, RetryResponse } from '../../services/fidelityPaymentService';

/**
 * Modal Step Types
 */
type ModalStep = 'amount' | 'customer' | 'review' | 'processing' | 'result';

/**
 * Fidelity Payment State
 */
interface FidelityPaymentState {
  // Payment initialization
  initializeLoading: boolean;
  initializeError: string | null;
  currentPayment: any;

  // Payment status
  statusLoading: boolean;
  statusError: string | null;
  paymentStatus: any;

  // Payment history
  historyLoading: boolean;
  historyError: string | null;
  paymentHistory: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };

  // Retry
  retryLoading: boolean;
  retryError: string | null;

  // UI state
  isModalOpen: boolean;
  modalStep: ModalStep;
}

/**
 * History Options
 */
interface HistoryOptions {
  status?: string;
  page?: number;
  limit?: number;
}

/**
 * Async Thunk: Create Virtual Account
 */
export const createVirtualAccount = createAsyncThunk<
  PaymentResponse,
  PaymentData
>(
  'fidelityPayment/createVirtualAccount',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await FidelityPaymentService.createVirtualAccount(paymentData);
      if (!response.success) {
        return rejectWithValue(response.error);
      }
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);



/**
 * Async Thunk: Get Payment Status
 */
export const getPaymentStatus = createAsyncThunk<
  PaymentStatusResponse,
  string
>(
  'fidelityPayment/getStatus',
  async (transactionRef, { rejectWithValue }) => {
    try {
      const response = await FidelityPaymentService.getPaymentStatus(transactionRef);
      if (!response.success) {
        return rejectWithValue(response.error);
      }
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Async Thunk: Get Payment History
 */
export const getPaymentHistory = createAsyncThunk<
  PaymentHistoryResponse,
  HistoryOptions
>(
  'fidelityPayment/getHistory',
  async ({ limit = 10, page = 1, status } = {}, { rejectWithValue }) => {
    try {
      const response = await FidelityPaymentService.getPaymentHistory({ limit, page, status });
      if (!response.success) {
        return rejectWithValue(response.error);
      }
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Async Thunk: Retry Payment
 */
export const retryPayment = createAsyncThunk<
  RetryResponse,
  string
>(
  'fidelityPayment/retry',
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await FidelityPaymentService.retryPayment(paymentId);
      if (!response.success) {
        return rejectWithValue(response.error);
      }
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);

const initialState: FidelityPaymentState = {
  // Payment initialization
  initializeLoading: false,
  initializeError: null,
  currentPayment: null,

  // Payment status
  statusLoading: false,
  statusError: null,
  paymentStatus: null,

  // Payment history
  historyLoading: false,
  historyError: null,
  paymentHistory: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },

  // Retry
  retryLoading: false,
  retryError: null,

  // UI state
  isModalOpen: false,
  modalStep: 'amount'
};

const fidelityPaymentSlice = createSlice({
  name: 'fidelityPayment',
  initialState,
  reducers: {
    openPaymentModal: (state) => {
      state.isModalOpen = true;
      state.modalStep = 'amount';
      state.initializeError = null;
      state.currentPayment = null;
    },
    closePaymentModal: (state) => {
      state.isModalOpen = false;
      state.modalStep = 'amount';
      state.initializeError = null;
      state.retryError = null;
      state.currentPayment = null;
    },
    setModalStep: (state, action: PayloadAction<ModalStep>) => {
      state.modalStep = action.payload;
    },
    clearError: (state) => {
      state.initializeError = null;
      state.statusError = null;
      state.historyError = null;
      state.retryError = null;
    },
    resetCurrentPayment: (state) => {
      state.currentPayment = null;
    }
  },
  extraReducers: (builder) => {
    // Create Virtual Account
    builder
      .addCase(createVirtualAccount.pending, (state) => {
        state.initializeLoading = true;
        state.initializeError = null;
      })
      .addCase(
        createVirtualAccount.fulfilled,
        (state, action: PayloadAction<PaymentResponse>) => {
          state.initializeLoading = false;
          state.currentPayment = action.payload;
          state.modalStep = 'result';
        }
      )
      .addCase(createVirtualAccount.rejected, (state, action) => {
        state.initializeLoading = false;
        state.initializeError = action.payload as string;
        state.modalStep = 'result';
      });



    // Get Payment Status
    builder
      .addCase(getPaymentStatus.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })
      .addCase(
        getPaymentStatus.fulfilled,
        (state, action: PayloadAction<PaymentStatusResponse>) => {
          state.statusLoading = false;
          state.paymentStatus = action.payload.data;
          if (state.currentPayment && action.payload.data) {
            state.currentPayment.status = action.payload.data.status;
          }
        }
      )
      .addCase(getPaymentStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.statusError = action.payload as string;
      });

    // Get Payment History
    builder
      .addCase(getPaymentHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(
        getPaymentHistory.fulfilled,
        (state, action: PayloadAction<PaymentHistoryResponse>) => {
          state.historyLoading = false;
          if (action.payload.data) {
            state.paymentHistory = action.payload.data.payments;
            state.pagination = {
              page: action.payload.data.pagination.currentPage || 1,
              limit: 10, // Default limit since it's not returned by API
              total: action.payload.data.pagination.totalRecords || 0,
              pages: action.payload.data.pagination.totalPages || 1
            };
          }
        }
      )
      .addCase(getPaymentHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload as string;
      });

    // Retry Payment
    builder
      .addCase(retryPayment.pending, (state) => {
        state.retryLoading = true;
        state.retryError = null;
      })
      .addCase(retryPayment.fulfilled, (state, action: PayloadAction<RetryResponse>) => {
        state.retryLoading = false;
        state.currentPayment = action.payload.data;
      })
      .addCase(retryPayment.rejected, (state, action) => {
        state.retryLoading = false;
        state.retryError = action.payload as string;
      });
  }
});

export const {
  openPaymentModal,
  closePaymentModal,
  setModalStep,
  clearError,
  resetCurrentPayment
} = fidelityPaymentSlice.actions;

export default fidelityPaymentSlice.reducer;
export type { FidelityPaymentState, ModalStep };
